import React, { useEffect, useRef } from "react";
import { useApp } from 'common/context';
import { useSuperState } from 'helpers/hooks';
import { ConnectionStatus, PlayerProvider } from "pages/player";

const CONNECTION_STATE = {
    CONNECTING: 0,
    CONNECTED: 1,
    DISCONNECTED: 2,
    FAILED: 3
};

export const Player = props => {

    const { spectatorUrl, match, sessionGuid } = props;

    const { api, setLocale, fleets } = useApp();

    const connection_monitor_interval_ref = useRef(null);
    const appstream_embed_ref = useRef(null);
    const container_ref = useRef(null);

    const report_interval_ref = useRef(null);
    const ping_interval_ref = useRef(null);
    const stats_buffer_ref = useRef([]);
    const session_guid_ref = useRef(null);

    const [state, setState] = useSuperState({
        session: null,
        avg_load_time: null,
        max_load_time: null,
        url: spectatorUrl || null,
        connection_state: CONNECTION_STATE.CONNECTING,
        visible: false,
        error: null,
        session_guid: sessionGuid || match?.params?.session_guid
    });

    const { connection_state, visible, url, session_guid, avg_load_time, max_load_time, session } = state;

    // Keep ref in sync so intervals always read the current value
    session_guid_ref.current = session_guid;

    const fetchSession = async () => {

        try {

            const { session } = await api.get(`/api/appstream/get/session/${session_guid}`);
            const { appstream_url, sms_ctag } = session;

            const fleet = fleets?.find(({ appstream_fleet_name }) => appstream_fleet_name === session?.appstream_fleet_name);

            setState({
                session,
                avg_load_time: fleet?.avg_session_load_time || 60,
                max_load_time: fleet?.max_session_load_time || 120,
                url: appstream_url
            });

            if (sms_ctag) setLocale(sms_ctag);

        } catch (e){

            const error = e?.err || e?.message || e;
            console.error(error);

            setState({
                error,
                connection_state: CONNECTION_STATE.FAILED
            });

        }

    };

    const initializeAppStream = () => {

        if (!url || !window.AppStream || appstream_embed_ref.current) return;

        const appstreamOptions = {
            sessionURL: url,
            userInterfaceConfig: {
                [window.AppStream.Embed.Options.HIDDEN_ELEMENTS]: [window.AppStream.Embed.Elements.TOOLBAR]
            }
        };

        appstream_embed_ref.current = new window.AppStream.Embed(
            container_ref.current,
            appstreamOptions
        );

        // Optional: Add event listeners
        appstream_embed_ref.current.addEventListener(
            window.AppStream.Embed.Events.SESSION_STATE_CHANGE,
            event => {

                console.log("Session state changed:", event);

                if (event?.sessionStatus == 'Started') {
                    setState({
                        connection_state: CONNECTION_STATE.CONNECTED,
                        visible: true
                    });
                    startStatsMonitoring();
                }

                if (event?.sessionStatus == 'Disconnected') {
                    setState({
                        connection_state: CONNECTION_STATE.DISCONNECTED,
                        visible: false
                    });
                    stopStatsMonitoring();
                }

            }
        );

        appstream_embed_ref.current.addEventListener(
            window.AppStream.Embed.Events.SESSION_ERROR,
            event => {
                console.log("Session error:", event);
                if (event?.errorCode == 404) {
                    setState({
                        error: event?.toString()
                    });
                }
            }
        );

        // After the timeout set the session as started regardless
        setTimeout(() => {

            if (connection_state === CONNECTION_STATE.CONNECTING) setState({ visible: true });

        }, max_load_time * 1000);

    };

    const cleanupAppStream = () => {

        const embed = appstream_embed_ref?.current;
        if (!embed) return;

        embed.destroy();
        appstream_embed_ref.current = null;

    };

    const startStatsMonitoring = () => {

        stopStatsMonitoring();

        console.log('[ConnectionMetrics] Starting HTTP ping monitoring, session_guid:', session_guid_ref.current);

        const rttWindow = [];
        let smoothedJitter = 0;
        let lastRtt = null;
        let pingsSent = 0;
        let pingsFailed = 0;

        // Ping /health every 5 seconds — lightweight HEAD request with connection reuse
        ping_interval_ref.current = setInterval(async () => {

            pingsSent++;

            try {

                const start = performance.now();
                const response = await fetch('/health', { method: 'HEAD' });
                const rtt = performance.now() - start;

                if (!response.ok) {

                    pingsFailed++;
                    return;

                }

                rttWindow.push(rtt);

                // Keep a rolling window of 12 samples (last 60 seconds)
                if (rttWindow.length > 12) {

                    rttWindow.shift();

                }

                // RFC 3550 exponential moving average jitter
                if (lastRtt !== null) {

                    const diff = Math.abs(rtt - lastRtt);
                    smoothedJitter += (diff - smoothedJitter) / 16;

                }
                lastRtt = rtt;

                // Need at least 2 samples
                if (rttWindow.length < 2) return;

                // RTT: median of window
                const sorted = [...rttWindow].sort((a, b) => a - b);
                const medianRtt = sorted[Math.floor(sorted.length / 2)];

                // Packet loss: percentage of failed pings in this reporting window
                const lossPct = pingsSent > 0 ? (pingsFailed / pingsSent) * 100 : 0;

                const sample = {
                    rtt_ms: medianRtt,
                    packet_loss_pct: lossPct,
                    jitter_ms: smoothedJitter,
                    frame_drop_pct: 0
                };

                stats_buffer_ref.current.push(sample);

            } catch (e) {

                pingsFailed++;

            }

        }, 5000);

        // Report averaged metrics to server every 30 seconds
        report_interval_ref.current = setInterval(() => {

            const buffer = stats_buffer_ref.current;
            const guid = session_guid_ref.current;

            if (!buffer.length || !guid) return;

            const avg = key => {

                const valid = buffer.map(s => s[key]).filter(v => v != null);
                return valid.length > 0 ? valid.reduce((a, b) => a + b, 0) / valid.length : null;

            };

            const payload = {
                session_guid: guid,
                rtt_ms: avg('rtt_ms'),
                packet_loss_pct: avg('packet_loss_pct'),
                jitter_ms: avg('jitter_ms'),
                frame_drop_pct: avg('frame_drop_pct')
            };

            stats_buffer_ref.current = [];

            // Reset loss counters each report cycle
            pingsSent = 0;
            pingsFailed = 0;

            console.log('[ConnectionMetrics] Reporting:', {
                rtt: payload.rtt_ms != null ? Math.round(payload.rtt_ms) + 'ms' : null,
                jitter: payload.jitter_ms != null ? Math.round(payload.jitter_ms * 10) / 10 + 'ms' : null,
                loss: payload.packet_loss_pct != null ? Math.round(payload.packet_loss_pct * 10) / 10 + '%' : null,
                samples: buffer.length
            });

            api.post('/api/appstream/sessions/connection-metrics', payload)
                .catch(err => console.error('[ConnectionMetrics] Report error:', err));

        }, 30000);

    };

    const stopStatsMonitoring = () => {

        if (connection_monitor_interval_ref.current)
            clearInterval(connection_monitor_interval_ref.current);
        connection_monitor_interval_ref.current = null;

        if (report_interval_ref.current)
            clearInterval(report_interval_ref.current);
        report_interval_ref.current = null;

        if (ping_interval_ref.current)
            clearInterval(ping_interval_ref.current);
        ping_interval_ref.current = null;

        stats_buffer_ref.current = [];

    };

    useEffect(() => {

        if (!!spectatorUrl) return;
        fetchSession();

        return () => {

            cleanupAppStream();

        };

    }, [spectatorUrl]);

    useEffect(() => {

        if (!url) return;
        initializeAppStream();

    }, [url]);

    const context = {
        connection_state,
        avg_load_time,
        max_load_time,
        session
    };

    return (
        <PlayerProvider value={context}>
            {!visible && (
                <div
                    className="position-absolute overflow-hidden"
                    style={{
                        width: '100vw',
                        height: '100vh',
                        left: 0,
                        top: 0
                    }}
                    >
                    <ConnectionStatus />
                </div>

            )}

            <div
                ref={container_ref}
                className="overflow-hidden position-absolute"
                style={{
                    width: '100vw',
                    height: '100vh',
                    opacity: visible ? 1 : 0,
                    left: 0,
                    top: 0
                }}
                tabIndex={spectatorUrl && -1} // Make the player unfocusable if spectating
            />
        </PlayerProvider>
    );
};
